import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';
import {
  setDashboardStats,
  setTopProfiles,
  setTrendingProfiles,
  setActiveSpotlightedProfiles,
  setSpotlightFeed,
  setLocationFeed,
  setKlyqueFeed,
  setMyFavorites,
  setFavoriteStats,
  setKlyque,
  setKlicks,
  setFollowStats,
  setSuggestedFollows,
  toggleProfileFavorite as toggleProfileFavoriteAction,
  updateFollowStatus as updateFollowStatusAction,
  setStatsLoading,
  setFeedLoading,
  setFollowLoading,
  setError,
  incrementFeedOffset,
  updatePostContent,
} from '../slices/dashboardSlice';
import { updateCurrentPostContent } from '../slices/postSlice';
import type { RootState } from '../Store';
import { toggleDiscoveryFavorite } from '../slices/discoverySlice';

// ===== Dashboard Stats Thunks =====
export const fetchDashboardStats = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setStatsLoading(true));
      const response = await api.get('/dashboard/stats');
      dispatch(setDashboardStats(response.data.stats));
      return response.data.stats;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch dashboard stats';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchTopProfiles = createAsyncThunk(
  'dashboard/fetchTopProfiles',
  async (limit: number = 5, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/profiles/top', { params: { limit } });
      dispatch(setTopProfiles(response.data.profiles));
      return response.data.profiles;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch top profiles';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchTrendingProfiles = createAsyncThunk(
  'dashboard/fetchTrendingProfiles',
  async (limit: number = 10, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/profiles/trending', { params: { limit } });
      dispatch(setTrendingProfiles(response.data.profiles));
      return response.data.profiles;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch trending profiles';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

/**
 * Active profile spotlights — currently live paid promotions. Drives
 * the dashboard "Spotlighted Right Now" carousel. Reuses the existing
 * `/dashboard/spotlight/active?type=profile` endpoint, mapping the
 * spotlight + nested content into the same Profile shape the carousel
 * already consumes (with `spotlightEndTime` for the countdown).
 */
export const fetchActiveSpotlightedProfiles = createAsyncThunk(
  'dashboard/fetchActiveSpotlightedProfiles',
  async (limit: number = 10, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/spotlight/active', { params: { type: 'profile', limit } });
      const profiles = (response.data.spotlights ?? [])
        .filter((s: any) => s.content && s.content.creator)
        .map((s: any) => ({
          id: s.id,
          profileId: s.content.profileId,
          priceKlv: s.content.priceKlv,
          metadataUri: s.content.metadataUri,
          creator: s.content.creator,
          spotlightEndTime: s.endTime,
        }));
      dispatch(setActiveSpotlightedProfiles(profiles));
      return profiles;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch active spotlights';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== Feed Thunks =====
export const fetchSpotlightFeed = createAsyncThunk(
  'dashboard/fetchSpotlightFeed',
  async ({ limit = 20, offset = 0, append = false }: { limit?: number; offset?: number; append?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFeedLoading(true));
      const response = await api.get('/dashboard/feed/spotlight', { params: { limit, offset } });
      dispatch(setSpotlightFeed({ posts: response.data.posts, hasMore: response.data.hasMore, append }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch spotlight feed';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchLocationFeed = createAsyncThunk(
  'dashboard/fetchLocationFeed',
  async ({ limit = 20, offset = 0, append = false, radius = 10, unit = 'miles' }: { limit?: number; offset?: number; append?: boolean; radius?: number; unit?: 'miles' | 'km' }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFeedLoading(true));
      const response = await api.get('/dashboard/feed/location', { params: { limit, offset, radius, unit } });
      dispatch(setLocationFeed({
        posts: response.data.posts,
        hasMore: response.data.hasMore,
        append,
        userLocation: response.data.userLocation
      }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch location feed';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchKlyqueFeed = createAsyncThunk(
  'dashboard/fetchKlyqueFeed',
  async ({ limit = 20, offset = 0, append = false }: { limit?: number; offset?: number; append?: boolean }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFeedLoading(true));
      const response = await api.get('/dashboard/feed/klyque', { params: { limit, offset } });
      dispatch(setKlyqueFeed({ posts: response.data.posts, hasMore: response.data.hasMore, append }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch Klyque feed';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchCombinedFeed = createAsyncThunk(
  'dashboard/fetchCombinedFeed',
  async (limitPerTab: number = 10, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFeedLoading(true));
      const response = await api.get('/dashboard/feed/combined', { params: { limitPerTab } });
      dispatch(setSpotlightFeed({ posts: response.data.spotlight, hasMore: true, append: false }));
      dispatch(setLocationFeed({ posts: response.data.nearYou, hasMore: true, append: false }));
      dispatch(setKlyqueFeed({ posts: response.data.klyque, hasMore: true, append: false }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch combined feed';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// Load more posts for active feed
export const loadMoreFeed = createAsyncThunk(
  'dashboard/loadMoreFeed',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { activeFeedTab, feedOffset, locationRadius, locationUnit } = state.dashboard;

      const newOffset = feedOffset + 20;

      switch (activeFeedTab) {
        case 'spotlight':
          await dispatch(fetchSpotlightFeed({ offset: newOffset, append: true }));
          break;
        case 'location':
          await dispatch(fetchLocationFeed({ offset: newOffset, append: true, radius: locationRadius, unit: locationUnit }));
          break;
        case 'klyque':
          await dispatch(fetchKlyqueFeed({ offset: newOffset, append: true }));
          break;
      }

      dispatch(incrementFeedOffset());
      return { offset: newOffset };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to load more posts';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== Favorites Thunks =====
export const toggleProfileFavorite = createAsyncThunk(
  'dashboard/toggleProfileFavorite',
  async (profileId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post('/favorites/toggle', { profileId });
      const favorited: boolean = response.data.favorited;
      dispatch(toggleProfileFavoriteAction({ profileId, favorited }));
      // Discovery list lives in its own slice; flip the matching profile
      // there too so the heart on the search card updates without a
      // refetch (and matches what the server will return on next load).
      dispatch(toggleDiscoveryFavorite({ profileId, favorited }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle favorite';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchMyFavorites = createAsyncThunk(
  'dashboard/fetchMyFavorites',
  async ({ limit = 20, offset = 0 }: { limit?: number; offset?: number }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/favorites/my-favorites', { params: { limit, offset } });
      dispatch(setMyFavorites(response.data.profiles));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch favorites';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchFavoriteStats = createAsyncThunk(
  'dashboard/fetchFavoriteStats',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/favorites/stats');
      dispatch(setFavoriteStats(response.data));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch favorite stats';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== Follow Thunks =====
export const fetchKlyque = createAsyncThunk(
  'dashboard/fetchKlyque',
  async ({ limit = 20, offset = 0 }: { limit?: number; offset?: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFollowLoading(true));
      const response = await api.get('/follow/klyque', { params: { limit, offset } });
      dispatch(setKlyque(response.data.klyque));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch Klyque';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchKlicks = createAsyncThunk(
  'dashboard/fetchKlicks',
  async ({ limit = 20, offset = 0 }: { limit?: number; offset?: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setFollowLoading(true));
      const response = await api.get('/follow/klicks', { params: { limit, offset } });
      dispatch(setKlicks(response.data.klicks));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch Klicks';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const toggleFollow = createAsyncThunk(
  'dashboard/toggleFollow',
  async ({ targetUserId, user }: { targetUserId: string; user?: { userId: string; username: string; displayName: string; avatar: string } }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.post('/follow/toggle', { targetUserId });
      dispatch(updateFollowStatusAction({
        userId: targetUserId,
        following: response.data.following,
        user: response.data.following ? user : undefined // Only include user data when following
      }));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle follow';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchFollowStats = createAsyncThunk(
  'dashboard/fetchFollowStats',
  async (targetUserId: string | undefined, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/follow/stats', { params: { targetUserId } });
      dispatch(setFollowStats(response.data));
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch follow stats';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchSuggestedFollows = createAsyncThunk(
  'dashboard/fetchSuggestedFollows',
  async (limit: number = 10, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.get('/follow/suggestions', { params: { limit } });
      dispatch(setSuggestedFollows(response.data.suggestions));
      return response.data.suggestions;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch suggested follows';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== Spotlight Thunks =====
export const purchaseSpotlight = createAsyncThunk(
  'dashboard/purchaseSpotlight',
  async (
    data: {
      spotlightType: 'profile' | 'post' | 'message';
      contentId: string;
      duration: number;
      tokenId?: string;
      txHash: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await api.post('/dashboard/spotlight/purchase', data);
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to purchase spotlight';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

// ===== Post Interaction Thunks =====
export const createPost = createAsyncThunk(
  'dashboard/createPost',
  async ({ body, mediaUrl, locationId }: { body: string; mediaUrl?: string; locationId?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/posts', { body, mediaUrl, locationId });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create post';
      return rejectWithValue(message);
    }
  }
);

export const togglePostLike = createAsyncThunk(
  'dashboard/togglePostLike',
  async (postId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/posts/${postId}/like`);
      return { postId, ...response.data };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle like';
      return rejectWithValue(message);
    }
  }
);

export const togglePostDislike = createAsyncThunk(
  'dashboard/togglePostDislike',
  async (postId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/posts/${postId}/dislike`);
      return { postId, ...response.data };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to toggle dislike';
      return rejectWithValue(message);
    }
  }
);

// ===== Comment Thunks =====
export const fetchPostComments = createAsyncThunk(
  'dashboard/fetchPostComments',
  async (
    { postId, limit = 20, offset = 0 }: { postId: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(`/comments/post/${postId}`, { params: { limit, offset } });
      return { postId, ...response.data };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to fetch comments';
      return rejectWithValue(message);
    }
  }
);

export const createComment = createAsyncThunk(
  'dashboard/createComment',
  async ({ postId, body }: { postId: string; body: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/comments/post/${postId}`, { body });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to create comment';
      return rejectWithValue(message);
    }
  }
);

export const updateComment = createAsyncThunk(
  'dashboard/updateComment',
  async ({ commentId, body }: { commentId: string; body: string }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/comments/${commentId}`, { body });
      return response.data;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update comment';
      return rejectWithValue(message);
    }
  }
);

export const deleteComment = createAsyncThunk(
  'dashboard/deleteComment',
  async (commentId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/comments/${commentId}`);
      return { commentId };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to delete comment';
      return rejectWithValue(message);
    }
  }
);

// ===== Post edit (#292) =====
// PATCH /posts/:id — server enforces owner check + returns the
// updated body/mediaUrl/preview fields. On success we mirror the
// change into every feed slice that might hold the post AND the
// post-detail slice so the card re-renders without a refetch.
interface EditPostArgs {
  postId: string;
  body: string;
  mediaUrl?: string | null;
  previewMode?: 'none' | 'char_limit' | 'text_only';
  previewCharLimit?: number;
  isAiGenerated?: boolean;
}
export const editPost = createAsyncThunk(
  'dashboard/editPost',
  async (args: EditPostArgs, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.patch(`/posts/${args.postId}`, {
        body: args.body,
        mediaUrl: args.mediaUrl ?? null,
        previewMode: args.previewMode,
        previewCharLimit: args.previewCharLimit,
        isAiGenerated: args.isAiGenerated,
      });
      const updated = response.data?.post ?? {};
      const patch = {
        postId: args.postId,
        body: updated.body ?? args.body,
        mediaUrl: updated.mediaUrl ?? args.mediaUrl ?? null,
        previewMode: updated.previewMode ?? args.previewMode,
        previewCharLimit: updated.previewCharLimit ?? args.previewCharLimit ?? null,
        isAiGenerated:
          typeof updated.isAiGenerated === 'boolean'
            ? updated.isAiGenerated
            : args.isAiGenerated,
        updatedAt: updated.updatedAt,
      };
      dispatch(updatePostContent(patch));
      dispatch(updateCurrentPostContent(patch));
      return updated;
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to update post';
      return rejectWithValue(message);
    }
  },
);
